import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Box, Droplets, Scale, User, LogIn, AlertCircle, CheckCircle, Package, Plus, Trash2, Layers } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyBin = {
  bin_number: '',
  doccia_number: 1,
  kg: '',
  status: null // null, 'checking', 'ok', 'error'
};

// Funzione per ottenere data/ora italiana corrente in formato datetime-local
const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const OperatoreIn = () => {
  const [bins, setBins] = useState([{ ...emptyBin }]);
  const [commonData, setCommonData] = useState({
    fornitore_nome: '',
    id_fornitore: '',
    prodotto_nome: '',
    lotto_number: '',
    note: '',
    data_inizio: getItalianDateTime()  // Data/ora ingresso impostata dall'operatore
  });
  const [loading, setLoading] = useState(false);
  const [prodotti, setProdotti] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [idFornitori, setIdFornitori] = useState([]);

  useEffect(() => {
    axios.get(`${API}/prodotti`).then(res => setProdotti(res.data)).catch(() => {});
    axios.get(`${API}/fornitori`).then(res => setFornitori(res.data)).catch(() => {});
    axios.get(`${API}/id-fornitori`).then(res => setIdFornitori(res.data)).catch(() => {});
  }, []);

  // Verifica bin
  const checkBin = useCallback(async (index, binNumber) => {
    if (!binNumber || binNumber.length < 1) {
      updateBin(index, { status: null });
      return;
    }

    updateBin(index, { status: 'checking' });
    try {
      const response = await axios.get(`${API}/bins/check/${binNumber}`);
      const data = response.data;
      if (data.exists && data.kg_attuali > 0 && data.status === 'in_depuration') {
        updateBin(index, { status: 'error', errorMsg: `Già in uso con ${data.kg_attuali} kg` });
      } else {
        updateBin(index, { status: 'ok' });
      }
    } catch (error) {
      updateBin(index, { status: 'ok' }); // Bin non esiste = libero
    }
  }, []);

  const updateBin = (index, updates) => {
    setBins(prev => prev.map((b, i) => i === index ? { ...b, ...updates } : b));
  };

  const addBin = () => {
    setBins(prev => [...prev, { ...emptyBin }]);
  };

  const removeBin = (index) => {
    if (bins.length > 1) {
      setBins(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleBinChange = (index, field, value) => {
    updateBin(index, { [field]: value });
    if (field === 'bin_number') {
      // Debounce check
      setTimeout(() => checkBin(index, value), 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione
    const validBins = bins.filter(b => b.bin_number && b.kg && b.status !== 'error');
    if (validBins.length === 0) {
      toast.error('Inserisci almeno un bin valido');
      return;
    }

    if (!commonData.fornitore_nome) {
      toast.error('Seleziona un fornitore');
      return;
    }

    if (!commonData.id_fornitore) {
      toast.error('Seleziona un ID');
      return;
    }

    setLoading(true);
    
    try {
      // Invia richieste per ogni bin
      const promises = validBins.map(bin => 
        axios.post(`${API}/richieste-importazione`, {
          tipo: 'ingresso',
          bin_number: bin.bin_number,
          doccia_number: parseInt(bin.doccia_number),
          kg: parseFloat(bin.kg),
          fornitore_nome: commonData.fornitore_nome,
          id_fornitore: commonData.id_fornitore,
          prodotto_nome: commonData.prodotto_nome,
          lotto_number: commonData.lotto_number,
          note: commonData.note,
          data_inizio: commonData.data_inizio  // Data/ora ingresso impostata dall'operatore
        })
      );
      
      await Promise.all(promises);
      
      toast.success(`${validBins.length} richieste ingresso inviate!`);
      setBins([{ ...emptyBin }]);
      setCommonData({
        fornitore_nome: '',
        id_fornitore: '',
        prodotto_nome: '',
        lotto_number: '',
        note: '',
        data_inizio: getItalianDateTime()  // Reset alla data corrente
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante invio');
    } finally {
      setLoading(false);
    }
  };

  const totalKg = bins.reduce((sum, b) => sum + (parseFloat(b.kg) || 0), 0);
  const validBinsCount = bins.filter(b => b.bin_number && b.kg && b.status !== 'error').length;

  return (
    <div className="max-w-lg mx-auto space-y-6" data-testid="operatore-in-page">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#22C55E]/20 rounded-2xl mb-4">
          <LogIn className="w-8 h-8 text-[#22C55E]" />
        </div>
        <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Ingresso Merce</h2>
        <p className="text-sm text-white/50 mt-2">Registra nuovo carico in depurazione</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Dati comuni */}
        <div className="bg-white/10 backdrop-blur border border-white/20 shadow-xl rounded-2xl p-4 space-y-4" data-testid="common-data-section">
          <div className="flex items-center gap-2 text-sm font-bold text-[#FFE500] uppercase">
            <Package className="w-4 h-4" />
            Dati Comuni per tutti i Bins
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Fornitore *</label>
              <select
                value={commonData.fornitore_nome}
                onChange={(e) => setCommonData({ ...commonData, fornitore_nome: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm"
                required
                data-testid="fornitore-select"
              >
                <option value="" className="bg-[#0A1628]">Seleziona...</option>
                {fornitori.map(f => (
                  <option key={f.nome} value={f.nome} className="bg-[#0A1628]">{f.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">ID *</label>
              <select
                value={commonData.id_fornitore}
                onChange={(e) => setCommonData({ ...commonData, id_fornitore: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-cyan-500/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                required
                data-testid="id-fornitore-select"
              >
                <option value="" className="bg-[#0A1628]">Seleziona...</option>
                {idFornitori.map(idf => (
                  <option key={idf.codice} value={idf.codice} className="bg-[#0A1628]">
                    {idf.codice}{idf.descrizione ? ` - ${idf.descrizione}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Prodotto</label>
              <select
                value={commonData.prodotto_nome}
                onChange={(e) => setCommonData({ ...commonData, prodotto_nome: e.target.value })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm"
                data-testid="prodotto-select"
              >
                <option value="" className="bg-[#0A1628]">Seleziona...</option>
                {prodotti.map(p => (
                  <option key={p.nome} value={p.nome} className="bg-[#0A1628]">{p.nome} {p.sigla && `[${p.sigla}]`}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Numero Lotto</label>
            <input
              type="text"
              value={commonData.lotto_number}
              onChange={(e) => setCommonData({ ...commonData, lotto_number: e.target.value })}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm"
              placeholder="es. L100"
              data-testid="lotto-input"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">Data e Ora Ingresso *</label>
            <input
              type="datetime-local"
              value={commonData.data_inizio}
              onChange={(e) => setCommonData({ ...commonData, data_inizio: e.target.value })}
              className="w-full px-3 py-2 bg-white/10 border border-[#22C55E]/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm"
              required
              data-testid="data-inizio-input"
            />
          </div>
        </div>

        {/* Lista Bins */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-white">
              <Layers className="w-4 h-4 text-[#22C55E]" />
              Bins da inserire ({bins.length})
            </div>
            <button
              type="button"
              onClick={addBin}
              className="flex items-center gap-1 px-3 py-1 bg-[#22C55E]/20 text-[#22C55E] rounded-lg text-xs font-bold hover:bg-[#22C55E]/30 transition-colors"
              data-testid="add-bin-btn"
            >
              <Plus className="w-3 h-3" />
              Aggiungi Bin
            </button>
          </div>

          {bins.map((bin, index) => (
            <div 
              key={index} 
              className={`bg-white/10 backdrop-blur border rounded-2xl p-4 ${
                bin.status === 'error' ? 'border-[#EF4444]' : 'border-white/20'
              }`}
              data-testid={`bin-row-${index}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 grid grid-cols-3 gap-3">
                  {/* Bin Number */}
                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">Bin</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={bin.bin_number}
                        onChange={(e) => handleBinChange(index, 'bin_number', e.target.value)}
                        className={`w-full px-3 py-2 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 text-sm ${
                          bin.status === 'error' ? 'border-[#EF4444] focus:ring-[#EF4444]' : 
                          bin.status === 'ok' ? 'border-[#22C55E] focus:ring-[#22C55E]' : 
                          'border-white/20 focus:ring-[#22C55E]'
                        }`}
                        placeholder="N° Bin"
                        data-testid={`bin-number-input-${index}`}
                      />
                      {bin.status === 'checking' && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        </div>
                      )}
                      {bin.status === 'ok' && (
                        <CheckCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#22C55E]" />
                      )}
                      {bin.status === 'error' && (
                        <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#EF4444]" />
                      )}
                    </div>
                    {bin.status === 'error' && bin.errorMsg && (
                      <p className="text-[10px] text-[#EF4444] mt-1">{bin.errorMsg}</p>
                    )}
                  </div>

                  {/* Doccia */}
                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">Doccia</label>
                    <select
                      value={bin.doccia_number}
                      onChange={(e) => handleBinChange(index, 'doccia_number', e.target.value)}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm"
                      data-testid={`doccia-select-${index}`}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num} className="bg-[#0A1628]">{num}</option>
                      ))}
                    </select>
                  </div>

                  {/* KG */}
                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">KG</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bin.kg}
                      onChange={(e) => handleBinChange(index, 'kg', e.target.value)}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm"
                      placeholder="0.00"
                      data-testid={`kg-input-${index}`}
                    />
                  </div>
                </div>

                {/* Remove button */}
                {bins.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBin(index)}
                    className="mt-6 p-2 text-[#EF4444] hover:bg-[#EF4444]/20 rounded-lg transition-colors"
                    data-testid={`remove-bin-btn-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Riepilogo e Submit */}
        <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-white/70">Bins validi:</span>
            <span className="text-lg font-bold text-[#22C55E]">{validBinsCount}</span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-white/70">Totale KG:</span>
            <span className="text-2xl font-bold text-[#22C55E]">{totalKg.toFixed(2)} kg</span>
          </div>
          
          <button
            type="submit"
            disabled={loading || validBinsCount === 0}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#22C55E] text-white rounded-xl font-bold uppercase tracking-wide shadow-lg shadow-[#22C55E]/20 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#22C55E]/90 transition-colors"
            data-testid="submit-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Invia {validBinsCount} Richieste Ingresso
              </>
            )}
          </button>
        </div>

        {/* Note */}
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-4">
          <label className="block text-xs font-medium text-white/70 mb-2">Note (opzionale)</label>
          <textarea
            value={commonData.note}
            onChange={(e) => setCommonData({ ...commonData, note: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#22C55E] text-sm resize-none"
            placeholder="Note aggiuntive..."
            data-testid="note-textarea"
          />
        </div>
      </form>
    </div>
  );
};

export default OperatoreIn;
